import { atomWithStorage } from 'jotai/utils';
import { startCase } from 'lodash-es';

const DEFAULT_PAGE_SIZE = 64;

const renderTransactionType = (transaction: { type: string }) => {
  const transactionType = transaction['type'];
  return startCase(transactionType.split('::').pop());
};

const numberFormat = new Intl.NumberFormat('en-US');
const format = {
  number(number: unknown) {
    return typeof number === 'number' ? numberFormat.format(number) : '';
  },
};

/**
 * Format a decimal number with commas and as much precision as needed.
 * @param {string | number} number Number to format.
 * @return {string} The decimal, formatted with full precision and commas.
 */
function formatDecimalFull(number: string | number): string {
  // NOTE: `numberFormat.format` DOES intentionally accept a string argument, but TypeScript still marks this as an error if we try to pass a string in, so `as any` is there to get around it.
  // this is actually fixed in the es2023 compiler
  return numberFormat.format(number as any);
}

const FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const DECIMAL_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const DECIMAL_FORMATTER_TWO_FRACTIONS = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DECIMAL_FORMATTER_THREE_FRACTIONS = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const PERCENT_FORMATTER_TWO_DECIMALS = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const pageSizeAtom = atomWithStorage(
  'list-pagination-size',
  DEFAULT_PAGE_SIZE,
  undefined,
  { getOnInit: true }
);

const buildNumberFormatter = (
  style: 'currency' | 'decimal' | 'percent',
  includeCommas: boolean,
  maxPrecision: number,
  minPrecision?: number
) => {
  return new Intl.NumberFormat('en-US', {
    style,
    currency: 'USD',
    minimumFractionDigits: minPrecision ?? maxPrecision,
    maximumFractionDigits: maxPrecision,
    useGrouping: includeCommas,
  });
};

const appendQueryParams = (
  url: string,
  params: Record<string, unknown>
): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  if (!queryString) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}`;
};

export {
  renderTransactionType,
  buildNumberFormatter,
  format,
  formatDecimalFull,
  FORMATTER,
  DECIMAL_FORMATTER,
  DECIMAL_FORMATTER_TWO_FRACTIONS,
  DECIMAL_FORMATTER_THREE_FRACTIONS,
  PERCENT_FORMATTER_TWO_DECIMALS,
  pageSizeAtom,
  DEFAULT_PAGE_SIZE,
  appendQueryParams,
};
